import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Container,
  Tabs,
  Tab,
  Stack,
} from "@mui/material";
import { supabase } from "../supabaseClient";

const AuthTabs = () => {
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [message, setMessage] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setMessage("");

    const { data, error } = await supabase.auth.signInWithPassword({
      email: loginForm.email,
      password: loginForm.password,
    });

    if (error) return setMessage(error.message);

    // 🔥 Let MainRouter re-evaluate user type
    window.location.reload();
  };

  const darkTextFieldProps = {
    fullWidth: true,
    sx: {
      width: "100%",
      backgroundColor: "#2a2d3a",
      borderRadius: "8px",
      mb: 2,
      "& .MuiOutlinedInput-root": {
        "& fieldset": { border: "1px solid rgba(255,255,255,0.1)" },
        "&:hover fieldset": { border: "1px solid rgba(255,255,255,0.2)" },
        "&.Mui-focused fieldset": { border: "2px solid #ffffff" },
      },
      "& .MuiInputLabel-root": {
        color: "rgba(255,255,255,0.7)",
        "&.Mui-focused": { color: "#ffffff" },
      },
      "& .MuiInputBase-input": {
        color: "#ffffff",
        padding: "12px 14px",
      },
    },
  };

  const modernButtonStyle = {
    bgcolor: "#ffffff",
    color: "#000000",
    "&:hover": {
      bgcolor: "#f0f0f0",
      transform: "translateY(-1px)",
      boxShadow: "0 4px 12px rgba(255,255,255,0.2)",
    },
    width: "100%",
    height: "48px",
    fontSize: "1rem",
    fontWeight: 600,
    textTransform: "uppercase",
    borderRadius: "8px",
    transition: "all 0.3s ease",
    mt: 2,
  };

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{
        width: "100vw",
        height: "100vh",
        display: "flex",
        flexDirection: { xs: "column", lg: "row" },
        backgroundColor: "#000000",
      }}
    >
      {/* LEFT LOGIN PANEL */}
      <Box
        sx={{
          width: { xs: "100%", lg: "450px" },
          bgcolor: "#1a1d29",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          p: 4,
        }}
      >
        <Typography variant="h4" sx={{ mb: 3, fontWeight: "bold", color: "#EAEAEA" }}>
          Infosight<span style={{ color: "#4D96FF" }}>AI</span>
        </Typography>

        <Tabs
          value={0}
          centered
          sx={{
            mb: 3,
            "& .MuiTab-root": { color: "#fff", fontWeight: 700 },
            "& .MuiTabs-indicator": { backgroundColor: "#ffffff", height: "3px" },
          }}
        >
          <Tab label="LOGIN" />
        </Tabs>

        <Box sx={{ width: "100%", maxWidth: "320px" }}>
          <form onSubmit={handleLogin}>
            <TextField
              name="email"
              label="Email Address"
              type="email"
              value={loginForm.email}
              onChange={(e) => setLoginForm((p) => ({ ...p, email: e.target.value }))}
              {...darkTextFieldProps}
            />

            <TextField
              name="password"
              label="Password"
              type="password"
              value={loginForm.password}
              onChange={(e) => setLoginForm((p) => ({ ...p, password: e.target.value }))}
              {...darkTextFieldProps}
            />

            <Button type="submit" variant="contained" sx={modernButtonStyle}>
              Log In
            </Button>
          </form>

          {message && (
            <Typography
              sx={{
                mt: 2,
                textAlign: "center",
                color: message.includes("successful") ? "#4ade80" : "#f87171",
              }}
            >
              {message}
            </Typography>
          )}
        </Box>
      </Box>

      {/* RIGHT PANEL */}
      <Box
        sx={{
          flex: 1,
          minHeight: { xs: "100vh", lg: "auto" },
          bgcolor: "#000000",
          color: "#ffffff",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          overflow: "hidden",
          p: { xs: 3, sm: 4, md: 6 },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background:
              "radial-gradient(circle at 20% 20%, rgba(255,255,255,0.03) 0%, transparent 50%)," +
              "radial-gradient(circle at 80% 80%, rgba(255,255,255,0.02) 0%, transparent 50%)," +
              "linear-gradient(135deg, transparent 0%, rgba(255,255,255,0.01) 100%)",
            zIndex: 1,
          }}
        />

        <Stack spacing={{ xs: 4, sm: 5, md: 6 }} sx={{ width: "100%", maxWidth: "800px", textAlign: "center", position: "relative", zIndex: 2 }}>
          <Box>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 900,
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem", lg: "3.5rem" },
                background: "linear-gradient(135deg, #ffffff 0%, #cccccc 100%)",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                letterSpacing: "-0.02em",
                mb: 2,
              }}
            >
              ALE-X
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 300, opacity: 0.9 }}>
              Your Intelligent Enterprise Assistant
            </Typography>
          </Box>

          <Box
            sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 4, mt: 4 }}
          >
            {[
              {
                title: "CONNECTED INTELLIGENCE",
                text: "Integrated with SAP and Zoho HR for instant, context-aware responses across your enterprise systems.",
              },
              {
                title: "MULTI-PLATFORM ACCESS",
                text: "Available in Web App, Google Chat, and Microsoft Teams. Work where you want, when you want.",
              },
              {
                title: "SMART OUTPUTS",
                text: "Generate structured tables, download links, and actionable insights—all in real-time conversations.",
              },
              {
                title: "VOICE ENABLED",
                text: "Hands-free interactions with voice commands and verbal responses for seamless workflows.",
              },
            ].map((f, i) => (
              <Box
                key={i}
                sx={{
                  p: 3,
                  border: "1px solid rgba(255,255,255,0.1)",
                  background: "rgba(255,255,255,0.02)",
                  transition: "all 0.3s ease",
                  "&:hover": { background: "rgba(255,255,255,0.05)", transform: "translateY(-2px)" },
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  {f.title}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, lineHeight: 1.6 }}>
                  {f.text}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ mt: 6 }}>
            <Typography variant="body1" sx={{ opacity: 0.7 }}>
              Experience the future of enterprise productivity
            </Typography>
          </Box>
        </Stack>
      </Box>
    </Container>
  );
};

export default AuthTabs;
